import Big from 'big.js';
import React, { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { DerivativesPositionSide } from 'common/api/autogenerated/trading-gateway';

import { LocalizedNumberInput } from 'common/components';
import { FormFieldInfo } from 'common/components/FormFieldInfo/FormFieldInfo.component';
import { LocalizeNumberInputProps } from 'common/components/LocalizedNumberInput/LocalizedNumberInput.component';

import { decimalAddition, localizeNumber } from 'common/helpers';
import { useDerivatives, useI18n } from 'common/hooks';
import { calculateStopLossPnl } from 'common/popups/FuturePositionAddTPSLPopup/helpers';

import { Dropdown, DropdownOption } from 'components/Dropdown/Dropdown.component';

import { PositionPnlTooltip } from 'views/derivatives/orders/open/positions/edit/components/PositionFee/PositionFee.component';
import { getPnlSign } from 'views/derivatives/orders/utils';

import { revertNumber } from 'utils/revertNumber';

import { DerivativeOrderFormType, DerivativesPriceType } from '../../types';
import classNames from './classNames';

interface StopLossFieldProps extends Omit<LocalizeNumberInputProps, 'name' | 'value'> {
  currency: string | null | undefined;
}

export const StopLossField: React.FC<React.PropsWithChildren<StopLossFieldProps>> = ({
  currency,
  digits,
  validate,
  deps,
  ...rest
}) => {
  const { translate } = useI18n();

  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext<DerivativeOrderFormType>();

  const stopLossTriggerPriceType = useWatch({
    control,
    name: 'stopLossTriggerPriceType',
  });
  const stopLoss = useWatch({ control, name: 'stopLoss' });
  const quantity = useWatch({ control, name: 'quantity' }) ?? 0;
  const tradingPairCode = useWatch({ control, name: 'tradingPairCode' });
  const takerFeeRate = useWatch({ control, name: 'takerFeeRate' }) ?? 0;

  const price = useWatch({ control, name: 'targetPrice' }) ?? 0;
  const side = useWatch({ control, name: 'action' }) || DerivativesPositionSide.BUY;

  const {
    stream: { quoteCurrency, settlementCurrency },
    info: { digitsForPandL },
  } = useDerivatives(tradingPairCode);

  const priceTypes = useMemo(
    () => [
      {
        value: DerivativesPriceType.MARK,
        label: translate.toText('mobile:derivative:form:tpsl:mark'),
      },
      {
        value: DerivativesPriceType.LAST,
        label: translate.toText('mobile:derivative:form:tpsl:last'),
      },
    ],
    [translate],
  );

  const stopLossLabelWithCurrency = useMemo(
    () => `${translate.toText('mobile:derivative:form:stop-loss:label')}, ${currency}`,
    [currency, translate],
  );

  const stopLossError = useMemo(() => errors.stopLoss?.message, [errors.stopLoss?.message]);

  const tradingFee = useMemo(
    () => revertNumber(new Big(Number(quantity)).times(Number(price)).times(Number(takerFeeRate)).div(100).toNumber()),
    [price, quantity, takerFeeRate],
  );

  const stopLossPnl = useMemo(() => {
    const size = Number(quantity);

    if (!Number(stopLoss) || !size) return 0;

    return calculateStopLossPnl({
      quantity,
      tradingPairCode,
      side,
      settlementCurrency,
      entryPrice: price,
      stopLossPrice: stopLoss,
    });
  }, [price, quantity, settlementCurrency, side, stopLoss, tradingPairCode]);

  const stopLossPnlWithFee = useMemo(
    () => decimalAddition(stopLossPnl, tradingFee).toNumber(),
    [stopLossPnl, tradingFee],
  );

  const stopLossPnlFormatted = useMemo(() => {
    if (!stopLossPnl) return '--.--';

    const formatted = localizeNumber(stopLossPnlWithFee, {
      minDigits: digitsForPandL,
    });
    return [getPnlSign(stopLossPnlWithFee, formatted), formatted];
  }, [digitsForPandL, stopLossPnl, stopLossPnlWithFee]);

  const onSelectStopLossTriggerPriceType = useCallback(
    (option: DropdownOption<DerivativesPriceType>) =>
      setValue('stopLossTriggerPriceType', option.value, { shouldTouch: true }),
    [setValue],
  );

  const options = useMemo(
    () => (
      <Dropdown
        icon={undefined} // 85523 TEMP CODE
        disabled // 85523 TEMP CODE
        //
        // icon={svgIcons.ChevronDown()}
        title={translate.toText('mobile:derivative:form:tp:title')}
        options={priceTypes}
        placement='bottom'
        onSelect={onSelectStopLossTriggerPriceType}
        value={stopLossTriggerPriceType}
      >
        <span className='Body2_reg_14'>
          {translate.toText(`mobile:derivative:form:tpsl:${stopLossTriggerPriceType}`)}
        </span>
      </Dropdown>
    ),
    [onSelectStopLossTriggerPriceType, priceTypes, stopLossTriggerPriceType, translate],
  );

  const tooltip = useMemo(
    () =>
      stopLossPnl ? (
        <PositionPnlTooltip
          digits={2}
          tradingFee={tradingFee}
          // 85523 TEMP CODE (markPrice -> lastPrice)
          markPriceUnrealizedPnL={stopLossPnl}
          liquidationPenalty={0}
          currency={quoteCurrency}
          labels={{
            pnl: translate.toText('mobile:derivative:position:details:closed:pnl:tooltip:open'),
            total: translate.toText('mobile:derivative:position:details:closed:pnl:tooltip:total'),
          }}
        />
      ) : undefined,
    [quoteCurrency, stopLossPnl, tradingFee, translate],
  );

  return (
    <div className='flex flex-column gap-8'>
      <LocalizedNumberInput
        showSteps
        label={stopLossLabelWithCurrency}
        digits={digits}
        value={stopLoss}
        error={stopLossError}
        options={options}
        validate={validate}
        deps={deps}
        {...rest}
        name='stopLoss'
      />
      <FormFieldInfo className='derivative-position-order-edit__pnl'>
        <FormFieldInfo.Label>
          {translate.toText('mobile:derivative:position:popup:close-partially:estimated-pnl:label')}
        </FormFieldInfo.Label>
        <FormFieldInfo.Value tooltip={tooltip} className={classNames.pnl(stopLossPnl)}>
          {stopLossPnlFormatted} {quoteCurrency}
        </FormFieldInfo.Value>
      </FormFieldInfo>
    </div>
  );
};
